import { type DataThemeOptions } from '@saviynt/design-system';
import { type FormikTouched } from 'formik';

import {
  EDITABLE_EXPRESSION_TYPES,
  EXPRESSIONS_TYPES,
  FIELD_TYPES,
  LAYOUT_TYPES,
  REQUIRED_EXPRESSION_TYPES,
  VISIBILITY_EXPRESSION_TYPES,
} from '../../../shared/constants';
import {
  type AlignmentTypes,
  type DataProvider,
  type DuplicateCheckConfig,
  type FieldElement,
  type FormValues,
  type MappingJSON,
  type PasswordField,
  type RichTextField,
} from '../../../shared/types';
import { evaluateExpression } from '../../ExpressionBuilder/utils';
import { type PreviewBannerType } from '../components/LocalPreviewAlerts/LocalPreviewAlerts';
import { type EntityInitialData } from '../data-store/initialDataSlice';

import {
  type DaVisibilityEvaluationContext,
  shouldDaDefaultVisibleWhenHidden,
} from './daVisibilityHelpers';

export interface VisibilityMaps {
  fieldVisibility: Map<string, boolean>; // fieldId -> isVisible
  sectionVisibility: Map<string, boolean>; // sectionId -> isVisible
  fieldToSection: Map<string, string>; // fieldId -> sectionId
  sectionFields: Map<string, string[]>; // sectionId -> fieldIds[]
  sectionHasVisibleFields: Map<string, boolean>; // sectionId -> hasVisibleContent
}

/** Effective UI visibility: field map AND parent section map (matches MetadataForm render rules). */
export const getEffectiveFieldVisibility = (
  fieldId: string,
  maps: VisibilityMaps
): boolean => {
  const fieldVisible = maps.fieldVisibility.get(fieldId) ?? true;
  const sectionId = maps.fieldToSection.get(fieldId);
  const sectionVisible = sectionId
    ? (maps.sectionVisibility.get(sectionId) ?? true)
    : true;

  return fieldVisible && sectionVisible;
};

export interface ComputeVisibilityMapsOptions {
  /** When true, apply DA-only ambiguous visibility default (show when all SHOW/HIDE sub-expressions are false). */
  isDaForm?: boolean;
}

export interface OnFormStateChangeTypes {
  isSubmittable: boolean;
  isValidating: boolean;
  dirty: boolean;
  errors: Record<string, unknown>;
}

export interface MetadataFormProps {
  canvasFields: FieldElement[];
  logo?: string;
  logoAlignment?: AlignmentTypes | null | undefined;
  urlSlug?: string;
  /** Custom form UID — used for DA form auto-detection */
  customFormUid?: string;
  /** Entity being edited (e.g. update route); forwarded to duplicate-check validation */
  entityId?: string;
  /**
   * Explicitly specify if this is a Dynamic Attribute form.
   * If provided, overrides auto-detection from urlSlug/customFormUid.
   * When true, enables DA-specific features (3-column layout, HTML labels).
   */
  isDynamicAttributeForm?: boolean;
  dataProviders?: Record<string, DataProvider>;
  onValuesChange?: (values: FormValues) => void;
  onSubmit?: (values: FormValues) => void;
  onFormStateChange?: (state: OnFormStateChangeTypes) => void;
  showBanner?: (type: PreviewBannerType, message?: string) => void;
  isPreview?: boolean;
  initialData?: EntityInitialData | null;
  /**
   * Prefilled form data for edit mode.
   * When provided, these values will be used as initial form values.
   * Use this for populating forms with existing data (e.g., edit mode in modals).
   */
  prefilledData?: Record<string, unknown> | null;
  isModalForm?: boolean;
  maxColumns?: number;
  passwordFieldRequiringConfirmation?: PasswordField | PasswordField[] | null;
  dataTheme?: DataThemeOptions;
  classNames?: {
    root?: string;
    section?: string;
    sectionFields?: string;
    logoWrapper?: string;
    logoImg?: string;
    row?: string;
  };
  dataTestId?: string;
  duplicateCheckConfig?: DuplicateCheckConfig;
  fieldMapping?: MappingJSON[];
  isPublicForm?: boolean;
  publicToken?: string | null;
  category?: string;
  /** Cloudflare Turnstile site key from the public form API response */
  captchaSiteKey?: string;
  /** Loaded entity field data for edit flows — blocks default auto-select
   *  when entity data exists. Passed from MFE (Redux) to avoid Redux dependency
   *  in NPM package consumers. Defaults to null (no blocking). */
  loadedFormElementsData?: Record<string, unknown> | null;
}

export const findPasswordFieldRequiringConfirmation = (
  canvasFields: FieldElement[]
): PasswordField | null => {
  const findInElements = (elements: FieldElement[]): PasswordField | null => {
    // eslint-disable-next-line no-restricted-syntax
    for (const element of elements) {
      if (
        element.type === 'field' &&
        element.fieldType === FIELD_TYPES.PASSWORD
      ) {
        const passwordField = element as PasswordField;

        if (passwordField.advConfig?.isPasswordConfirmation) {
          return passwordField;
        }
      }

      if (
        (element.type === 'section' || element.type === 'row') &&
        element.children
      ) {
        const found = findInElements(element.children);

        if (found) {
          return found;
        }
      }
    }

    return null;
  };

  return findInElements(canvasFields);
};

/**
 * Find ALL password fields that require confirmation.
 * Returns an array of PasswordField objects whose advConfig.isPasswordConfirmation is true.
 * Used for multi-password support in DA forms and other contexts.
 */
export const findPasswordFieldsRequiringConfirmation = (
  canvasFields: FieldElement[]
): PasswordField[] => {
  const results: PasswordField[] = [];

  const collectFromElements = (elements: FieldElement[]): void => {
    // eslint-disable-next-line no-restricted-syntax
    for (const element of elements) {
      if (
        element.type === 'field' &&
        element.fieldType === FIELD_TYPES.PASSWORD
      ) {
        const passwordField = element as PasswordField;

        if (passwordField.advConfig?.isPasswordConfirmation) {
          results.push(passwordField);
        }
      }

      if (
        (element.type === 'section' || element.type === 'row') &&
        element.children
      ) {
        collectFromElements(element.children);
      }
    }
  };

  collectFromElements(canvasFields);

  return results;
};

/**
 * Preview component for rendering text form fields with validation
 * Supports Formik integration and can be used standalone or within modals
 */
export const buildStructureMaps = (
  canvasFields: FieldElement[],
  maps: VisibilityMaps
): void => {
  const traverseElements = (
    elements: FieldElement[],
    currentSectionId?: string
  ) => {
    elements.forEach((element) => {
      if (element.type === LAYOUT_TYPES.SECTION) {
        // Initialize section in maps
        maps.sectionFields.set(element.id, []);
        maps.sectionVisibility.set(element.id, true);
        maps.sectionHasVisibleFields.set(element.id, false);

        // Traverse section children
        if (element.children) {
          traverseElements(element.children, element.id);
        }
      } else if (element.type === LAYOUT_TYPES.ROW) {
        // Traverse row children (rows don't have visibility conditions)
        if (element.children) {
          traverseElements(element.children, currentSectionId);
        }
      } else {
        // This is a field
        maps.fieldVisibility.set(element.id, true);

        if (currentSectionId) {
          maps.fieldToSection.set(element.id, currentSectionId);
          const sectionFields = maps.sectionFields.get(currentSectionId) ?? [];

          sectionFields.push(element.id);
          maps.sectionFields.set(currentSectionId, sectionFields);
        }
      }
    });
  };

  traverseElements(canvasFields);
};

export const computeFieldVisibility = (
  canvasFields: FieldElement[],
  values: FormValues,
  touched: FormikTouched<FormValues>,
  maps: VisibilityMaps,
  entityData: EntityInitialData | null,
  options?: ComputeVisibilityMapsOptions
): void => {
  const isDaForm = options?.isDaForm === true;
  const daContext: DaVisibilityEvaluationContext = {
    values,
    touched,
    entityData,
  };

  const evaluateFieldVisibility = (element: FieldElement): void => {
    if (
      element.type === LAYOUT_TYPES.SECTION ||
      element.type === LAYOUT_TYPES.ROW
    ) {
      if (element.children) {
        element.children.forEach(evaluateFieldVisibility);
      }
    } else {
      // This is a field
      let isVisible = !('isHidden' in element && element.isHidden);

      if (isVisible && element.fieldType === FIELD_TYPES.RICHTEXT) {
        const richField = element as RichTextField;
        const isEmpty =
          !richField.advConfig?.richText ||
          richField.advConfig.richText.ops.every((op) => {
            if (typeof op.insert === 'string') {
              return op.insert.trim() === '';
            }

            return false;
          });

        if (isEmpty) {
          isVisible = false;
        }
      }

      if (isVisible && 'expressions' in element) {
        const visibilityExpression = element.expressions?.find(
          (expr) => expr.type === EXPRESSIONS_TYPES.VISIBILITY
        );

        if (visibilityExpression?.enabled) {
          const visibleBeforeVisibilityExpr = isVisible;

          const hideValue = visibilityExpression.value.find(
            (v) => v.kind === VISIBILITY_EXPRESSION_TYPES.HIDE && v.selected
          );

          if (hideValue?.value) {
            const result = evaluateExpression(hideValue.value, {
              values,
              touched,
              entityData,
            });

            if (result) {
              isVisible = false;
            }
          }

          const showValue = visibilityExpression.value.find(
            (v) => v.kind === VISIBILITY_EXPRESSION_TYPES.SHOW && v.selected
          );

          if (showValue?.value) {
            const result = evaluateExpression(showValue.value, {
              values,
              touched,
              entityData,
            });

            isVisible = result;
          }

          if (
            isDaForm &&
            !isVisible &&
            visibleBeforeVisibilityExpr &&
            shouldDaDefaultVisibleWhenHidden(
              visibilityExpression,
              daContext,
              isDaForm
            )
          ) {
            isVisible = true;
          }
        }
      }

      maps.fieldVisibility.set(element.id, isVisible);
    }
  };

  canvasFields.forEach(evaluateFieldVisibility);
};

export const computeSectionVisibility = (
  canvasFields: FieldElement[],
  values: FormValues,
  touched: FormikTouched<FormValues>,
  maps: VisibilityMaps,
  entityData: EntityInitialData | null
): void => {
  const evaluateSectionVisibility = (element: FieldElement): void => {
    if (element.type === LAYOUT_TYPES.SECTION) {
      let isVisible = true;

      const visibilityExpression = element.expressions?.find(
        (expr) => expr.type === EXPRESSIONS_TYPES.VISIBILITY
      );

      if (visibilityExpression?.enabled) {
        const hideValue = visibilityExpression.value.find(
          (v) => v.kind === VISIBILITY_EXPRESSION_TYPES.HIDE && v.selected
        );

        if (hideValue?.value) {
          const result = evaluateExpression(hideValue.value, {
            values,
            touched,
            entityData,
          });

          if (result) {
            isVisible = false;
          }
        }

        const showValue = visibilityExpression.value.find(
          (v) => v.kind === VISIBILITY_EXPRESSION_TYPES.SHOW && v.selected
        );

        if (showValue?.value) {
          const result = evaluateExpression(showValue.value, {
            values,
            touched,
            entityData,
          });

          isVisible = result;
        }
      }

      maps.sectionVisibility.set(element.id, isVisible);

      if (element.children) {
        element.children.forEach(evaluateSectionVisibility);
      }
    } else if (element.type === LAYOUT_TYPES.ROW && element.children) {
      element.children.forEach(evaluateSectionVisibility);
    }
  };

  canvasFields.forEach(evaluateSectionVisibility);
};

export const computeSectionContentVisibility = (maps: VisibilityMaps): void => {
  maps.sectionFields.forEach((fieldIds, sectionId) => {
    const hasVisibleFields = fieldIds.some(
      (fieldId) => maps.fieldVisibility.get(fieldId) === true
    );

    maps.sectionHasVisibleFields.set(sectionId, hasVisibleFields);
  });
};

export const computeVisibilityMaps = (
  canvasFields: FieldElement[],
  values: FormValues,
  touched: FormikTouched<FormValues>,
  entityData: EntityInitialData | null,
  options?: ComputeVisibilityMapsOptions
): VisibilityMaps => {
  const maps: VisibilityMaps = {
    fieldVisibility: new Map(),
    sectionVisibility: new Map(),
    fieldToSection: new Map(),
    sectionFields: new Map(),
    sectionHasVisibleFields: new Map(),
  };

  buildStructureMaps(canvasFields, maps);
  computeFieldVisibility(
    canvasFields,
    values,
    touched,
    maps,
    entityData,
    options
  );
  computeSectionVisibility(canvasFields, values, touched, maps, entityData);
  computeSectionContentVisibility(maps);

  return maps;
};

// Helper function to determine if field is dynamically required
export const getDynamicRequiredState = (
  field: FieldElement,
  values: FormValues,
  touched: FormikTouched<FormValues>,
  entityData: EntityInitialData | null
): boolean | undefined => {
  // Check if field has isReadOnly property (only certain field types have it)
  if ('isReadOnly' in field && field.isReadOnly) {
    return undefined;
  }

  if (!('expressions' in field)) {
    return undefined;
  }

  const requiredExpression = field.expressions?.find(
    (expr) => expr.type === EXPRESSIONS_TYPES.REQUIRED
  );

  if (!requiredExpression?.enabled) {
    return undefined;
  }

  // Check REQUIRED condition
  const requiredValue = requiredExpression.value.find(
    (v) => v.kind === REQUIRED_EXPRESSION_TYPES.REQUIRED && v.selected
  );

  if (requiredValue?.value) {
    return evaluateExpression(requiredValue.value, {
      values,
      touched,
      entityData,
    });
  }

  // Check NOT_REQUIRED condition
  const notRequiredValue = requiredExpression.value.find(
    (v) => v.kind === REQUIRED_EXPRESSION_TYPES.NOT_REQUIRED && v.selected
  );

  if (notRequiredValue?.value) {
    return !evaluateExpression(notRequiredValue.value, {
      values,
      touched,
      entityData,
    });
  }

  return false;
};

// Helper function to compute readonly states for all fields at once
export const computeReadOnlyStates = (
  canvasFields: FieldElement[],
  values: FormValues,
  touched: FormikTouched<FormValues>,
  entityData: EntityInitialData | null
): Map<string, boolean | undefined> => {
  const readOnlyStates = new Map<string, boolean | undefined>();

  const processField = (field: FieldElement) => {
    if (field.type === 'field') {
      readOnlyStates.set(
        field.id,
        getDynamicReadOnlyState(field, values, touched, entityData)
      );
    }

    if ('children' in field && field.children) {
      field.children.forEach(processField);
    }
  };

  canvasFields.forEach(processField);

  return readOnlyStates;
};

// Helper function to compute required states for all fields at once
export const computeRequiredStates = (
  canvasFields: FieldElement[],
  values: FormValues,
  touched: FormikTouched<FormValues>,
  entityData: EntityInitialData | null
): Map<string, boolean | undefined> => {
  const requiredStates = new Map<string, boolean | undefined>();

  const processField = (field: FieldElement) => {
    if (field.type === 'field') {
      requiredStates.set(
        field.id,
        getDynamicRequiredState(field, values, touched, entityData)
      );
    }

    if ('children' in field && field.children) {
      field.children.forEach(processField);
    }
  };

  canvasFields.forEach(processField);

  return requiredStates;
};

// Helper function to determine if field is dynamically readonly
export const getDynamicReadOnlyState = (
  field: FieldElement,
  values: FormValues,
  touched: FormikTouched<FormValues>,
  entityData: EntityInitialData | null
): boolean | undefined => {
  // Skip if field is already statically readonly
  if ('isReadOnly' in field && field.isReadOnly) {
    return undefined; // Static readonly takes precedence
  }

  if (!('expressions' in field)) {
    return undefined;
  }

  const editableExpression = field.expressions?.find(
    (expr) => expr.type === EXPRESSIONS_TYPES.EDITABLE
  );

  if (!editableExpression?.enabled) {
    return undefined; // No dynamic readonly logic
  }

  // Check READONLY condition
  const readOnlyValue = editableExpression.value.find(
    (v) => v.kind === EDITABLE_EXPRESSION_TYPES.READONLY && v.selected
  );

  if (readOnlyValue?.value) {
    return evaluateExpression(readOnlyValue.value, {
      values,
      touched,
      entityData,
    });
  }

  // Check EDITABLE condition
  const editableValue = editableExpression.value.find(
    (v) => v.kind === EDITABLE_EXPRESSION_TYPES.EDITABLE && v.selected
  );

  if (editableValue?.value) {
    return !evaluateExpression(editableValue.value, {
      values,
      touched,
      entityData,
    });
  }

  return false;
};
